package br.com.htms.inspeccaocampo.platform

import br.com.htms.inspeccaocampo.auth.AuthApi
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch

/**
 * Cópia de segurança no SharePoint de todo relatório gerado no portal (PDF, Excel, CSV, PPTX).
 * O Worker monta Módulo/[Formulário]/Obra/Ano/Mês e acrescenta data/hora ao nome.
 */
object BackupSharePoint {
    /** Com [colaborador], o arquivo vai para Módulo/Colaborador/Formulário (sem obra/ano/mês). */
    data class Contexto(
        val modulo: String,
        val formulario: String = "",
        val obra: String = "",
        val colaborador: String = ""
    )

    /** Contexto da tela aberta, usado quando a exportação não informa um próprio. */
    var contextoPadrao: () -> Contexto? = { null }

    @PublishedApi internal var contextoExplicito: Contexto? = null
    @PublishedApi internal var ignorar = false

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
    private val extensoes = setOf("pdf", "xlsx", "xls", "csv", "pptx", "docx")

    /** Executa [bloco] (que chama [PlatformFiles.shareFile]) com módulo/formulário/obra definidos. */
    inline fun <T> com(
        modulo: String,
        formulario: String = "",
        obra: String = "",
        colaborador: String = "",
        bloco: () -> T
    ): T {
        val anterior = contextoExplicito
        contextoExplicito = Contexto(modulo, formulario, obra, colaborador)
        try {
            return bloco()
        } finally {
            contextoExplicito = anterior
        }
    }

    /** Re-download de arquivo que já existe no servidor ou modelo em branco: não vai ao backup. */
    inline fun <T> semBackup(bloco: () -> T): T {
        val anterior = ignorar
        ignorar = true
        try {
            return bloco()
        } finally {
            ignorar = anterior
        }
    }

    fun aoGerar(bytes: ByteArray, fileName: String, mimeType: String) {
        if (ignorar || bytes.isEmpty()) return
        val ext = fileName.substringAfterLast('.', "").lowercase()
        if (ext !in extensoes) return
        val ctx = contextoExplicito ?: runCatching { contextoPadrao() }.getOrNull() ?: return
        val caminho = "/sharepoint/backup?modulo=${codificar(ctx.modulo)}" +
            "&formulario=${codificar(ctx.formulario)}" +
            "&obra=${codificar(ctx.obra)}" +
            "&colaborador=${codificar(ctx.colaborador)}" +
            "&nome=${codificar(fileName)}"
        scope.launch {
            AuthApi.apiUploadBytes(caminho, bytes, mimeType.ifBlank { "application/octet-stream" })
        }
    }

    /** Admin: grava um arquivo de teste e confirma o isolamento da permissão. */
    suspend fun testarConexao(): Result<String> =
        AuthApi.apiUploadBytes("/sharepoint/teste", "teste".encodeToByteArray(), "text/plain")

    private fun codificar(valor: String): String = buildString {
        for (b in valor.trim().encodeToByteArray()) {
            val c = b.toInt() and 0xFF
            if ((c in 'a'.code..'z'.code) || (c in 'A'.code..'Z'.code) || (c in '0'.code..'9'.code) ||
                c == '-'.code || c == '_'.code || c == '.'.code || c == '~'.code
            ) {
                append(c.toChar())
            } else {
                append('%')
                append("0123456789ABCDEF"[c shr 4])
                append("0123456789ABCDEF"[c and 0xF])
            }
        }
    }
}
